import { ContentState } from "draft-js";
import { Api } from "@core/api/api";
import { organizationHeaders } from "@core/api/organizationHeaders";
import { filterParams, pageParams } from "@core/api/pageParams";
import { Page, PaginationOptions } from "@utils/page";
import { contentStateToString } from "@utils/richText/converters";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { PostDto, postFromDto } from "@domain/post/dtos";
import { Post } from "@domain/post/post";
import { Carousel, CarouselStatus } from "./carousel";
import { carouselFromDto } from "./dtos";

const CAROUSEL_URL = "/carousels";
const CAROUSEL_INSIGHTFUL_URL = "/instructives";

export class CarouselApi {
	constructor(private api: Api) {}

	async get(id: string): Promise<Carousel> {
		if (id.charAt(0) === "@") id = id.substring(1);
		const result = await this.api.get<Carousel>(`${CAROUSEL_URL}/${id}`);
		return carouselFromDto(result.data);
	}
	async getBySlug(slug: string): Promise<Carousel> {
		const result = await this.api.get<Carousel>(`${CAROUSEL_URL}?slug=${slug}`);
		return carouselFromDto(result.data);
	}

	async getSuggestions(
		pageOptions: PaginationOptions,
		categories: Category[],
		language: ContentLanguage
	): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}/suggestions?${pageParams(pageOptions)}&categories=${categories.join(",")}&language=${language}`
		);
		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async getSuggestionsById(pageOptions: PaginationOptions, carouselId: string): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}/suggestions?${pageParams(pageOptions)}&carouselId=${carouselId}`
		);

		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	deleteCarousel(carouselId: string, organizationId?: string): Promise<void> {
		return this.api.delete(`${CAROUSEL_URL}/${carouselId}`, organizationHeaders(organizationId));
	}

	async getFavorites(pageOptions: PaginationOptions): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(`${CAROUSEL_URL}?filter=favorite&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async getMyCarousels(status: CarouselStatus | "ALL", pageOptions: PaginationOptions): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}?filter=mine&status=${status === "ALL" ? "" : status}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async getProfileCarousels(profileId: string, pageOptions: PaginationOptions): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(`${CAROUSEL_URL}?userId=${profileId}&${pageParams(pageOptions)}`);
		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async getOrganizationCarousels(organizationId: string, pageOptions: PaginationOptions): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}?organizationId=${organizationId}&${pageParams(pageOptions)}`
		);
		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async createCarousel(draft: Carousel, organizationId?: string): Promise<Carousel> {
		const result = await this.api.post<Carousel>(CAROUSEL_URL, draft, organizationHeaders(organizationId));
		return carouselFromDto(result.data);
	}

	async saveCarousel(draft: Carousel, id: string, organizationId?: string): Promise<Carousel> {
		const result = await this.api.put<Carousel>(
			`${CAROUSEL_URL}/${id}`,
			{ ...draft, author: draft.author.id },
			organizationHeaders(organizationId)
		);
		return carouselFromDto(result.data);
	}

	async setFavorite(id: string): Promise<void> {
		await this.api.post<void>(`${CAROUSEL_URL}/${id}/favorite`);
	}

	async removeFavorite(id: string): Promise<void> {
		await this.api.delete<void>(`${CAROUSEL_URL}/${id}/favorite`);
	}

	async setInstructive(id: string): Promise<void> {
		return this.api.post(`${CAROUSEL_INSIGHTFUL_URL}/${id}/carousels`);
	}

	async removeInstructive(id: string): Promise<void> {
		return this.api.delete(`${CAROUSEL_INSIGHTFUL_URL}/${id}/carousels`);
	}

	async share(carouselId: string, message?: ContentState, groupId?: string): Promise<Post> {
		const result = await this.api.post<PostDto>(`${CAROUSEL_URL}/${carouselId}/share`, {
			text: message ? contentStateToString(message) : undefined,
			groupId,
		});
		return postFromDto(result.data);
	}

	view(carouselId: string): Promise<void> {
		return this.api.post(`${CAROUSEL_URL}/${carouselId}/view`);
	}

	async search(
		pageOptions: PaginationOptions,
		query: string,
		categories?: Category[],
		_levels?: ContentLevel[],
		languages?: ContentLanguage[]
	): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}?query=${query}&${pageParams(pageOptions)}&${filterParams(categories, undefined, languages)}`
		);
		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async deleteFeedback(carouselId: string): Promise<void> {
		await this.api.delete(`${CAROUSEL_URL}/${carouselId}/feedbacks`);
	}

	async getSuggestionsBySameAuthor(pageOptions: PaginationOptions, carouselId: string): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}/${carouselId}/same-author?${pageParams(pageOptions)}`
		);

		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}

	async getSuggestionsBySameCategory(pageOptions: PaginationOptions, carouselId: string): Promise<Page<Carousel>> {
		const result = await this.api.get<Page<Carousel>>(
			`${CAROUSEL_URL}/${carouselId}/same-author-category?${pageParams(pageOptions)}`
		);

		return { ...result.data, content: result.data.content.map((c) => carouselFromDto(c)) };
	}
}
